// ========================================
// pages/Settings.js - Death Verification Settings
// ========================================
// PROJECT: Agyapadie Vault - Digital Inheritance Platform
// LAST MODIFIED: April 12, 2026
// 
// PURPOSE: Allow users to configure their death verification threshold
// 
// VERIFICATION THRESHOLD EXPLANATION:
// - Determines how many trustees must confirm "DECEASED" before assets release
// - Example: "2 of 3 trustees" means 2 out of 3 trustees must confirm death
// 
// ASSETS USED:
// - Uses plan badges from plan logic (no external assets needed)
// 
// FOOTER: Included (full width, proper spacing)
// ========================================

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import Footer from '../components/Footer';

const Settings = () => {
  const navigate = useNavigate();
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verificationThreshold, setVerificationThreshold] = useState(1);
  const [allOptions, setAllOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const planCapabilities = {
    free: { maxTrustees: 2, availableThresholds: [1], upgradeMessage: 'Upgrade to Premium for more options' },
    premium: { maxTrustees: 3, availableThresholds: [1, 2], upgradeMessage: 'Upgrade to Business for more options' },
    business: { maxTrustees: 6, availableThresholds: [1, 2, 3], upgradeMessage: 'Upgrade to Enterprise for maximum flexibility' },
    enterprise: { maxTrustees: 10, availableThresholds: [1, 2, 3, 4], upgradeMessage: 'Maximum flexibility' }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/users/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUser(response.data.user);
      
      const allPlanOptions = [
        { threshold: 1, requiredPlan: 'free', maxTrustees: 2, description: '1 of 2 trustees' },
        { threshold: 1, requiredPlan: 'premium', maxTrustees: 3, description: '1 of 3 trustees' },
        { threshold: 2, requiredPlan: 'premium', maxTrustees: 3, description: '2 of 3 trustees' },
        { threshold: 1, requiredPlan: 'business', maxTrustees: 6, description: '1 of 6 trustees' },
        { threshold: 2, requiredPlan: 'business', maxTrustees: 6, description: '2 of 6 trustees' },
        { threshold: 3, requiredPlan: 'business', maxTrustees: 6, description: '3 of 6 trustees' },
        { threshold: 1, requiredPlan: 'enterprise', maxTrustees: 10, description: '1 of 10 trustees' },
        { threshold: 2, requiredPlan: 'enterprise', maxTrustees: 10, description: '2 of 10 trustees' },
        { threshold: 3, requiredPlan: 'enterprise', maxTrustees: 10, description: '3 of 10 trustees' },
        { threshold: 4, requiredPlan: 'enterprise', maxTrustees: 10, description: '4 of 10 trustees' }
      ];
      
      setAllOptions(allPlanOptions);
      setVerificationThreshold(response.data.user.verificationSettings?.deathThreshold || 1);
      
    } catch (err) {
      if (err.response?.status === 401) navigate('/login');
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveThreshold = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${process.env.REACT_APP_API_URL}/api/users/settings`,
        { deathThreshold: verificationThreshold },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setSuccess('Verification threshold updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const isOptionAvailable = (option) => {
    const planPriority = { free: 0, premium: 1, business: 2, enterprise: 3 };
    const currentPriority = planPriority[user?.plan] || 0;
    const requiredPriority = planPriority[option.requiredPlan];
    return currentPriority >= requiredPriority && option.threshold <= (user?.limits?.trustees || 2);
  };

  const getUpgradeTarget = (option) => {
    if (option.requiredPlan === 'premium') return 'Premium';
    if (option.requiredPlan === 'business') return 'Business';
    if (option.requiredPlan === 'enterprise') return 'Enterprise';
    return option.requiredPlan;
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#F5F0E6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="loading-spinner"></div>
          <p>Loading settings...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  const currentPlan = user.plan;
  const planInfo = planCapabilities[currentPlan];
  const canChoose = planInfo.availableThresholds.length > 1;

  return (
    <div style={{ minHeight: '100vh', background: '#F5F0E6', display: 'flex', flexDirection: 'column' }}>
      
      <div style={{ padding: '20px', flex: 1 }}>
        
        <div className="settings-header">
          <Link to="/dashboard" className="back-link">← Back to Dashboard</Link>
          <h1 className="settings-title">Verification Settings</h1>
          <div className="settings-placeholder"></div>
        </div>

        <div className="settings-container">
          
          {/* Plan Information */}
          <div className="settings-card">
            <h2 className="settings-card-title">📊 Your Plan</h2>
            <div className="plan-info">
              <span className={`plan-badge plan-badge-${user.plan}`}>{user.plan.toUpperCase()}</span>
              <p className="plan-description">
                {user.plan === 'free' && 'Free plan includes basic verification (1 of 2 trustees).'}
                {user.plan === 'premium' && 'Premium plan allows 1 or 2 of 3 trustees.'}
                {user.plan === 'business' && 'Business plan allows 1, 2, or 3 of 6 trustees.'}
                {user.plan === 'enterprise' && 'Enterprise plan offers 1-4 of 10 trustees.'}
              </p>
            </div>
          </div>

          {/* Verification Threshold Settings */}
          <div className="settings-card">
            <h2 className="settings-card-title">⚖️ Death Verification Threshold</h2>
            
            <p className="settings-description">
              Choose how many trustees must confirm <strong>"DECEASED"</strong> before your assets are released.
            </p>
            
            <div className="threshold-options">
              <label className="threshold-label">Select Verification Threshold</label>
              <div className="options-list">
                {allOptions.map((option, idx) => {
                  const isAvailable = isOptionAvailable(option);
                  const isCurrent = !isAvailable ? false : (verificationThreshold === option.threshold && option.maxTrustees === (user?.limits?.trustees || 2));
                  const upgradeTo = getUpgradeTarget(option);
                  
                  return (
                    <div 
                      key={idx}
                      className={`option-item ${isCurrent ? 'current' : ''} ${!isAvailable ? 'locked' : ''}`}
                      onClick={() => { if (isAvailable) setVerificationThreshold(option.threshold); }}
                    >
                      <div className="option-left">
                        <span className="option-text">{option.description}</span>
                        {!isAvailable && (
                          <Link to="/upgrade" className="upgrade-tag-link">
                            ↑ Upgrade to {upgradeTo}
                          </Link>
                        )}
                        {isAvailable && isCurrent && (
                          <span className="current-tag">✓ Current</span>
                        )}
                      </div>
                      <div className="option-right">
                        {isAvailable && !isCurrent && (
                          <button onClick={() => setVerificationThreshold(option.threshold)} className="select-btn">
                            Select
                          </button>
                        )}
                        {isAvailable && isCurrent && (
                          <span className="active-indicator">Active</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {canChoose && (
                <button onClick={handleSaveThreshold} disabled={saving} className="save-btn">
                  {saving ? 'Saving...' : 'Save Settings'}
                </button>
              )}
              
              {user.plan === 'free' && (
                <div className="upgrade-prompt">
                  <p>✨ <strong>Premium plans</strong> allow you to choose verification thresholds.</p>
                  <Link to="/upgrade" className="upgrade-now-btn">Upgrade Now →</Link>
                </div>
              )}
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}
          
        </div>
      </div>

      <Footer />

      <style dangerouslySetInnerHTML={{ __html: `
        
        .settings-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          max-width: 800px;
          margin: 0 auto 30px;
          width: 100%;
          flex-wrap: wrap;
          gap: 15px;
        }
        
        .back-link {
          color: #2E7D32;
          text-decoration: none;
          font-size: 14px;
        }
        
        .settings-title {
          font-size: 28px;
          color: #1A1A1A;
          margin: 0;
        }
        
        .settings-placeholder {
          width: 100px;
        }
        
        .settings-container {
          max-width: 800px;
          margin: 0 auto 30px;
          width: 100%;
        }
        
        .settings-card {
          background: white;
          border-radius: 12px;
          padding: 25px;
          margin-bottom: 25px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }
        
        .settings-card-title {
          font-size: 20px;
          color: #2E7D32;
          margin-bottom: 20px;
          padding-bottom: 10px;
          border-bottom: 2px solid #FFD700;
          display: inline-block;
        }
        
        .plan-info {
          display: flex;
          align-items: center;
          gap: 15px;
          flex-wrap: wrap;
        }
        
        .plan-badge {
          padding: 6px 16px;
          border-radius: 20px;
          font-weight: bold;
          font-size: 14px;
        }
        
        .plan-badge-free { background: #9CA3AF; color: white; }
        .plan-badge-premium, .plan-badge-business, .plan-badge-enterprise { background: #FFD700; color: #1A1A1A; }
        
        .plan-description {
          color: #666;
          font-size: 14px;
          margin: 0;
          flex: 1;
        }
        
        .settings-description {
          color: #666;
          font-size: 14px;
          margin-bottom: 25px;
          line-height: 1.5;
        }
        
        .threshold-label {
          display: block;
          font-size: 14px;
          font-weight: bold;
          color: #1A1A1A;
          margin-bottom: 15px;
        }
        
        .options-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 20px;
        }
        
        .option-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 14px;
          background: #F5F0E6;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s;
          border: 1px solid transparent;
        }
        
        .option-item:hover {
          transform: translateX(3px);
        }
        
        .option-item.current {
          background: #e8f4fd;
          border-color: #2E7D32;
        }
        
        .option-item.locked {
          opacity: 0.7;
          background: #f0f0f0;
          cursor: not-allowed;
        }
        
        .option-left {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }
        
        .option-text {
          font-size: 13px;
          font-weight: 500;
          color: #1A1A1A;
        }
        
        .upgrade-tag-link {
          font-size: 11px;
          padding: 3px 10px;
          background: #FFD700;
          color: #1A1A1A;
          border-radius: 20px;
          font-weight: bold;
          text-decoration: none;
          transition: all 0.3s;
          white-space: nowrap;
          display: inline-block;
        }
        
        .upgrade-tag-link:hover {
          background: #B8860B;
          transform: scale(1.02);
        }
        
        .current-tag {
          font-size: 11px;
          padding: 3px 10px;
          background: #2E7D32;
          color: white;
          border-radius: 20px;
          font-weight: bold;
          white-space: nowrap;
          display: inline-block;
        }
        
        .option-right {
          display: flex;
          align-items: center;
        }
        
        .select-btn {
          font-size: 12px;
          padding: 5px 14px;
          background: transparent;
          border: 1px solid #2E7D32;
          color: #2E7D32;
          border-radius: 20px;
          cursor: pointer;
          transition: all 0.3s;
          white-space: nowrap;
        }
        
        .select-btn:hover {
          background: #2E7D32;
          color: white;
        }
        
        .active-indicator {
          font-size: 12px;
          color: #2E7D32;
          font-weight: bold;
        }
        
        .save-btn {
          width: 100%;
          padding: 12px;
          background: #FFD700;
          color: #1A1A1A;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: bold;
          margin-top: 10px;
        }
        
        .save-btn:hover {
          background: #B8860B;
        }
        
        .upgrade-prompt {
          margin-top: 20px;
          padding: 15px;
          background: #fff3cd;
          border: 1px solid #ffc107;
          border-radius: 8px;
          text-align: center;
        }
        
        .upgrade-now-btn {
          display: inline-block;
          margin-top: 10px;
          padding: 8px 24px;
          background: #FFD700;
          color: #1A1A1A;
          text-decoration: none;
          border-radius: 5px;
          font-weight: bold;
        }
        
        .upgrade-now-btn:hover {
          background: #B8860B;
        }
        
        .error-message {
          background: #fee2e2;
          border: 1px solid #ef4444;
          color: #dc2626;
          padding: 12px;
          border-radius: 8px;
          margin-bottom: 20px;
        }
        
        .success-message {
          background: #d1fae5;
          border: 1px solid #10b981;
          color: #065f46;
          padding: 12px;
          border-radius: 8px;
          margin-bottom: 20px;
        }
        
        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #F5F0E6;
          border-top: 4px solid #FFD700;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @media (max-width: 768px) {
          .settings-header {
            flex-direction: column;
            text-align: center;
            margin-bottom: 20px;
          }
          
          .settings-title {
            font-size: 24px;
          }
          
          .settings-placeholder {
            display: none;
          }
          
          .settings-card {
            padding: 20px;
          }
          
          .option-item {
            flex-wrap: wrap;
            gap: 8px;
          }
          
          .option-left {
            flex: 1;
          }
          
          .upgrade-tag-link, .current-tag {
            white-space: normal;
            text-align: center;
          }
        }
        
        @media (max-width: 480px) {
          .settings-title {
            font-size: 20px;
          }
          
          .settings-card {
            padding: 15px;
          }
          
          .option-text {
            font-size: 12px;
          }
          
          .upgrade-tag-link, .current-tag, .select-btn {
            font-size: 10px;
            padding: 3px 8px;
          }
        }
        
      `}} />
    </div>
  );
};

export default Settings;